import { useNavigate } from 'react-router';
import { useState } from 'react';
import { ClearFiltersButton } from '../../components/clearFiltersButton.tsx';
import { ProtocolStatus } from '../../../shared/types/protocol.ts';

interface Props {
    isLoading: boolean;
    error: any;
    showDisabled: boolean;
    onlyWithInterval: boolean;
    filterInput: string;
    status: ProtocolStatus[] | undefined;
    setStatus: (value: ProtocolStatus[] | undefined) => void;
    setShowDisabled: (value: boolean) => void;
    setOnlyWithInterval: (value: boolean) => void;
    setFilterInput: (value: string) => void;
    onShowMappingsClick: () => void;
    handleClearFilters: () => void;
}

interface StatusFilterButton {
    name: string;
    opType: ProtocolStatus | undefined;
    enabled: boolean;
}

export function ProtocolListInputs({
    isLoading,
    error,
    showDisabled,
    onlyWithInterval,
    filterInput,
    status,
    setStatus,
    setShowDisabled,
    setOnlyWithInterval,
    setFilterInput,
    onShowMappingsClick,
    handleClearFilters,
}: Props) {
    const navigate = useNavigate();

    const handleCreateClick = () => {
        navigate(`/protocol-view?mode=edit`);
    };

    const [statusFilterButtons, setStatusFilterButtons] = useState<StatusFilterButton[]>([
        { name: 'All', opType: undefined, enabled: true },
        { name: 'Approved', opType: 'approved', enabled: true },
        { name: 'Draft', opType: 'draft', enabled: true },
        { name: 'Review', opType: 'review', enabled: true },
        { name: 'Rejected', opType: 'rejected', enabled: true },
    ]);

    const getToggleButtonStyle = (button: StatusFilterButton) => {
        const color = getStatusColor(button.opType);
        return button.enabled ? { backgroundColor: color, color: '#fff' } : { borderColor: color };
    };

    const onStatusFilterClick = (clicked: string) => {
        setStatusFilterButtons((prev) => {
            let next = [...prev];

            if (clicked === 'All') {
                const allItem = next.find((b) => b.name === 'All');
                allItem!.enabled = !allItem!.enabled; //Toggle enable

                next = next.map((btn) => ({ ...btn, enabled: allItem!.enabled })); // Set all to same value as All button
                setStatus(allItem!.enabled ? undefined : []);
            } else {
                const clickedItem = next.find((b) => b.name === clicked);
                clickedItem!.enabled = !clickedItem!.enabled; // Toggle clicked button

                // Check if all are enabled/disabled
                const allItemsEnabled = next.reduce((res, b) => (b.name !== 'All' ? res && b.enabled : res), true);
                if (allItemsEnabled) {
                    // Enable All item is its not enabled already and set status to undefined to include all statuses
                    const allItem = next.find((b) => b.name === 'All');
                    allItem!.enabled = true; // Toggle enable
                    setStatus(undefined); // Set status to undefined to include all statuses
                } else {
                    // Make sure All item is disabled if it is not already and set status to all enabled statuses or empty array if none enabled
                    const allItem = next.find((b) => b.name === 'All');
                    allItem!.enabled = false; //Toggle disable
                    setStatus(next.filter((b) => b.name !== 'All' && b.enabled).map((b) => b.opType as ProtocolStatus)); // Set status to all enabled statuses or empty array if none enabled
                }
            }
            return next;
        });
    };

    if (isLoading) return <div>Loading...</div>;
    if (error) return <div>An error has occurred: {error.message}</div>;
    return (
        <div className='button-bar'>
            <button onClick={handleCreateClick}>Create protocol</button>
            <button onClick={onShowMappingsClick}>Show mappings</button>

            <div className='flex__spacer' />
            <label className='with-checkbox' style={{ marginRight: '18px' }}>
                Only with interval
                <input
                    style={{ marginLeft: '8px' }}
                    type='checkbox'
                    checked={onlyWithInterval}
                    onChange={(e) => setOnlyWithInterval(e.target.checked)}
                />
            </label>
            <label className='with-checkbox'>
                Show disabled
                <input style={{ marginLeft: '8px' }} type='checkbox' checked={showDisabled} onChange={(e) => setShowDisabled(e.target.checked)} />
            </label>
            <input
                style={{ marginLeft: '18px' }}
                type='text'
                placeholder='Search'
                value={filterInput}
                onChange={(e) => setFilterInput(e.target.value)}
            />
            <ClearFiltersButton onClick={handleClearFilters} />
            {statusFilterButtons.map((button) => (
                <button
                    key={button.name}
                    className={'toggle-button'}
                    style={{ ...getToggleButtonStyle(button) }}
                    onClick={() => onStatusFilterClick(button.name)}
                >
                    {button.name}
                </button>
            ))}
        </div>
    );
}

function getStatusColor(statusType: ProtocolStatus | undefined): string {
    switch (statusType) {
        case 'approved':
            return '#32cd32';
        case 'draft':
            return '#ffea00';
        case 'review':
            return '#ffa500';
        case 'rejected':
            return '#cd2626';
        case undefined:
            return '#1e90ff'; // ie All
        default:
            throw new Error(`Unknown status type: ${statusType}`);
    }
}
